// src/modules/accounting/pages/JournalEntriesPage.jsx

import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Card, Spin, Alert, Typography, Row, Col, DatePicker, Button, Space,
  Select, Tag, Empty, Tooltip, Input, Modal, Descriptions, Divider,
  message, Segmented
} from 'antd';
import {
  ReloadOutlined, PlusOutlined, EyeOutlined, UndoOutlined,
  DeleteOutlined, ExclamationCircleOutlined, StopOutlined,
  ClockCircleOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import {
  getAuditTrail, reverseJournalEntry, deleteJournalEntry,
  listManualJournals, cancelManualJournal,
  getJournalActionRequests, getPendingJournalActions, cancelJournalActionRequest,
} from '../api';
import { JOURNAL_ENTRY_SOURCES, NATURE_MAP } from '../constants';
import ManualJournalModal from '../components/ManualJournalModal';
import DataTable from '../../../components/UI/DataTable';

const { Title, Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

const TYPE_COLORS = {
  ASSET: 'blue',
  LIABILITY: 'red',
  EQUITY: 'purple',
  REVENUE: 'green',
  EXPENSE: 'orange',
};

// Tag for an account's nature; falls back to the coarse type for legacy rows
// that were never classified.
const natureTag = (account) => {
  const def = NATURE_MAP[account?.nature];
  if (def) return <Tag color={TYPE_COLORS[def.type]}>{def.label}</Tag>;
  if (account?.type) return <Tag color={TYPE_COLORS[account.type]}>{account.type}</Tag>;
  return '—';
};

const VIEWS = ['posted', 'requests', 'actions'];

export default function JournalEntriesPage() {
  // ?view=requests|actions lets approval deep links land on the right tab.
  const [searchParams] = useSearchParams();
  const [data, setData] = useState({ entries: [], meta: {} });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(null);
  const [dateBasis, setDateBasis] = useState('TRANSACTION'); // which date the range filters on
  const [sourceFilter, setSourceFilter] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [page, setPage] = useState(1);
  const [detailModal, setDetailModal] = useState(null);
  const [view, setView] = useState(() => {
    const v = searchParams.get('view');
    return VIEWS.includes(v) ? v : 'posted';
  });
  const [manualOpen, setManualOpen] = useState(false);
  const [requests, setRequests] = useState({ data: [], meta: {} });
  const [reqLoading, setReqLoading] = useState(false);
  // Delete / reverse requests awaiting approval.
  const [actionRequests, setActionRequests] = useState({ data: [], meta: {} });
  const [actionLoading, setActionLoading] = useState(false);
  // { [journalEntryId]: { action, reference } } — badges the posted list so a
  // second request can't be raised on an entry that already has one in flight.
  const [pendingActions, setPendingActions] = useState({});

  const fetch = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: 30 };
      if (dateRange && dateRange[0] && dateRange[1]) {
        params.startDate = dateRange[0].startOf('day').toISOString();
        params.endDate = dateRange[1].endOf('day').toISOString();
        params.dateBasis = dateBasis;
      }
      if (sourceFilter) params.source = sourceFilter;
      if (statusFilter) params.status = statusFilter;
      const res = await getAuditTrail(params);
      setData(res);
    } catch (err) {
      setError(err.message || 'Failed to load journal entries');
    } finally {
      setLoading(false);
    }
  }, [dateRange, dateBasis, sourceFilter, statusFilter, page]);

  useEffect(() => { fetch(); }, [fetch]);

  const fetchRequests = useCallback(async () => {
    setReqLoading(true);
    try {
      const res = await listManualJournals({ page: 1, limit: 50 });
      setRequests({ data: res.data || [], meta: res.meta || {} });
    } catch (err) {
      message.error(err?.response?.data?.message || err.message || 'Failed to load manual journal requests');
    } finally {
      setReqLoading(false);
    }
  }, []);

  useEffect(() => { if (view === 'requests') fetchRequests(); }, [view, fetchRequests]);

  const fetchActionRequests = useCallback(async () => {
    setActionLoading(true);
    try {
      const [list, pending] = await Promise.all([
        getJournalActionRequests({ page: 1, limit: 50 }),
        getPendingJournalActions(),
      ]);
      setActionRequests({ data: list.data || [], meta: list.meta || {} });
      setPendingActions(pending.data || {});
    } catch (err) {
      message.error(err?.response?.data?.message || err.message || 'Failed to load delete/reverse requests');
    } finally {
      setActionLoading(false);
    }
  }, []);

  // The pending map is needed on the posted list too, not just its own tab —
  // that is what stops a second request being raised on the same entry.
  useEffect(() => { fetchActionRequests(); }, [fetchActionRequests]);

  const fmt = (v) => Number((v || 0).toFixed(2)).toLocaleString('en-IN');

  const statusColors = { POSTED: 'green', DRAFT: 'gold', REVERSED: 'red' };

  // Reversal linkage, resolved across the rows on screen. Auto-reversals (a deleted
  // smelter dispatch, a regenerated bill) come through as an ordinary POSTED entry,
  // so without this the pair reads as two unrelated entries.
  const reversalIndex = useMemo(() => {
    const byId = new Map();
    const reverses = new Map(); // reversal entry _id → the entryId it cancels
    (data.entries || []).forEach((e) => byId.set(String(e._id), e));
    (data.entries || []).forEach((e) => {
      if (e.reversedEntryId) reverses.set(String(e.reversedEntryId), e.entryId);
    });
    return {
      // what cancelled this entry
      reversedBy: (e) => (e.reversedEntryId ? byId.get(String(e.reversedEntryId))?.entryId || null : null),
      // what this entry cancels
      cancels: (e) => reverses.get(String(e._id)) || null,
    };
  }, [data.entries]);

  const apiError = (err) =>
    err?.response?.data?.message || err.message || 'Operation failed';

  /**
   * Delete and Reverse no longer act on the ledger — each raises a request that
   * goes through the same Approval Engine as a manual journal entry. The dialog
   * therefore collects a reason for the approver rather than confirming an
   * immediate, irreversible action.
   */
  const askForAction = (record, action) => {
    let reason = '';
    const isDelete = action === 'DELETE';

    Modal.confirm({
      title: `${isDelete ? 'Delete' : 'Reverse'} ${record.entryId} — send for approval`,
      icon: isDelete
        ? <ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />
        : <UndoOutlined />,
      width: 540,
      content: (
        <div>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Nothing happens yet"
            description={`This is submitted for approval. ${
              isDelete
                ? 'The entry is removed — and its fund, cash-drawer and treasury movements unwound — only once an approver signs it off.'
                : 'The counter-entry is posted only once an approver signs it off.'
            }`}
          />
          {isDelete && record.referenceId && (
            <Paragraph style={{ fontSize: 12, marginBottom: 12 }}>
              This is an <b>auto-generated</b> entry ({record.source}). On approval it
              cascade-rolls-back everything it created: the fund transaction(s), stored
              fund balances, the source record and any treasury mirrors.
            </Paragraph>
          )}
          {!isDelete && (
            <Paragraph style={{ fontSize: 12, marginBottom: 12 }}>
              A counter-entry will be posted (debits ↔ credits swapped) and this entry
              marked REVERSED. Both entries stay on record.
            </Paragraph>
          )}
          <Text strong style={{ fontSize: 12 }}>Reason for the approver</Text>
          <Input.TextArea
            rows={3}
            autoFocus
            placeholder={isDelete
              ? 'e.g. duplicate of JE-000008, entered twice by mistake'
              : 'e.g. wrong account picked, correcting via counter-entry'}
            onChange={(e) => { reason = e.target.value; }}
            style={{ marginTop: 4 }}
          />
        </div>
      ),
      okText: 'Submit for Approval',
      okButtonProps: { danger: isDelete },
      onOk: async () => {
        try {
          const res = isDelete
            ? await deleteJournalEntry(record._id, reason)
            : await reverseJournalEntry(record._id, reason);
          message.success(res.message || 'Submitted for approval');
          fetch();
          fetchActionRequests();
        } catch (err) {
          message.error(apiError(err));
          throw err;   // keep the dialog open so the reason isn't lost
        }
      },
    });
  };

  const handleReverse = (record) => askForAction(record, 'REVERSE');
  const handleDelete = (record) => askForAction(record, 'DELETE');

  const handleWithdrawAction = (record) => {
    Modal.confirm({
      title: `Withdraw ${record.reference}?`,
      icon: <StopOutlined />,
      content: `The pending ${record.action.toLowerCase()} of ${record.entryIdSnapshot} will be cancelled. The entry stays exactly as it is.`,
      okText: 'Withdraw',
      onOk: async () => {
        try {
          await cancelJournalActionRequest(record._id);
          message.success(`${record.reference} withdrawn`);
          fetchActionRequests();
          fetch();
        } catch (err) {
          message.error(apiError(err));
        }
      },
    });
  };

  const handleCancelRequest = (record) => {
    Modal.confirm({
      title: `Cancel manual entry ${record.reference}?`,
      icon: <StopOutlined />,
      content: 'The pending approval will be withdrawn. Nothing has been posted to the ledger yet.',
      okText: 'Withdraw',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          await cancelManualJournal(record._id);
          message.success(`${record.reference} cancelled`);
          fetchRequests();
        } catch (err) {
          message.error(apiError(err));
        }
      },
    });
  };

  const reqStatusColors = { PENDING: 'gold', APPROVED: 'green', REJECTED: 'red', CANCELLED: 'default', FAILED: 'volcano' };

  const requestColumns = [
    { title: 'Reference', dataIndex: 'reference', width: 130 },
    {
      title: <Tooltip title="When the activity happened. The entry date is stamped on the journal entry when approval posts it.">Txn Date</Tooltip>,
      dataIndex: 'date', width: 105,
      render: (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—',
    },
    { title: 'Narration', dataIndex: 'narration', ellipsis: true, render: (n) => n || '—' },
    { title: 'Amount', dataIndex: 'totalDebit', align: 'right', width: 120, render: (v) => `₹${fmt(v)}` },
    {
      title: 'Fund Sync', dataIndex: 'hasFundSync', width: 100, align: 'center',
      render: (v) => v ? <Tag color="blue">Fund</Tag> : <Tag>GL only</Tag>,
    },
    { title: 'Status', dataIndex: 'status', width: 110, render: (s) => <Tag color={reqStatusColors[s]}>{s}</Tag> },
    {
      title: 'Posted JE', dataIndex: 'journalEntry', width: 120,
      render: (je) => je?.entryId ? <Tag color="green">{je.entryId}</Tag> : '—',
    },
    {
      title: 'Actions', width: 90, render: (_, record) => (
        record.status === 'PENDING' ? (
          <Tooltip title="Withdraw pending request">
            <Button size="small" danger icon={<StopOutlined />} onClick={() => handleCancelRequest(record)} />
          </Tooltip>
        ) : null
      ),
    },
  ];

  const columns = [
    { title: 'Entry ID', dataIndex: 'entryId', width: 120 },
    {
      title: <Tooltip title="When the activity happened — this is the date every report uses">Txn Date</Tooltip>,
      dataIndex: 'date', width: 105,
      render: (d) => d ? dayjs(d).format('DD/MM/YYYY') : '—',
    },
    {
      title: <Tooltip title="When the entry was booked into the ledger">Entry Date</Tooltip>,
      dataIndex: 'entryDate', width: 105,
      render: (d, record) => {
        if (!d) return <Text type="secondary">—</Text>;
        // Same day for anything posted as it happened; only worth calling out when
        // the entry was booked later than the transaction it records.
        const deferred = record.date && !dayjs(d).isSame(dayjs(record.date), 'day');
        return deferred
          ? <Tooltip title={`Booked ${dayjs(d).diff(dayjs(record.date), 'day')} day(s) after the transaction`}>
              <Tag color="gold" style={{ marginInlineEnd: 0 }}>{dayjs(d).format('DD/MM/YYYY')}</Tag>
            </Tooltip>
          : <Text type="secondary">{dayjs(d).format('DD/MM/YYYY')}</Text>;
      },
    },
    { title: 'Source', dataIndex: 'source', width: 160, render: (s) => <Tag color="blue">{s}</Tag> },
    { title: 'Narration', dataIndex: 'narration', ellipsis: true },
    {
      title: 'Status', dataIndex: 'status', width: 165,
      render: (s, record) => {
        const reversedBy = reversalIndex.reversedBy(record);
        const cancels = reversalIndex.cancels(record);
        return (
          <Space size={4} wrap>
            <Tooltip title={reversedBy ? `Reversed by ${reversedBy}` : ''}>
              <Tag color={statusColors[s]} style={{ marginInlineEnd: 0 }}>{s}</Tag>
            </Tooltip>
            {cancels && (
              <Tooltip title={`Counter-entry that cancels ${cancels}`}>
                <Tag color="volcano" style={{ marginInlineEnd: 0 }}>↩ {cancels}</Tag>
              </Tooltip>
            )}
          </Space>
        );
      },
    },
    { title: 'Debit', dataIndex: 'totalDebit', render: (v) => `₹${fmt(v)}`, align: 'right', width: 120 },
    { title: 'Credit', dataIndex: 'totalCredit', render: (v) => `₹${fmt(v)}`, align: 'right', width: 120 },
    {
      title: 'Actions', width: 160, render: (_, record) => {
        // An entry with a request already in flight offers neither button —
        // the backend refuses a second one anyway, so don't invite the click.
        const pending = pendingActions[String(record._id)];
        return (
          <Space>
            <Tooltip title="View details">
              <Button size="small" icon={<EyeOutlined />} onClick={() => setDetailModal(record)} />
            </Tooltip>

            {pending ? (
              <Tooltip title={`${pending.reference} — ${pending.action.toLowerCase()} awaiting approval`}>
                <Tag color="gold" icon={<ClockCircleOutlined />} style={{ marginInlineEnd: 0 }}>
                  {pending.action === 'DELETE' ? 'Delete' : 'Reverse'} pending
                </Tag>
              </Tooltip>
            ) : (
              <>
                {record.status === 'POSTED' && (
                  <Tooltip title="Reverse — sends a request for approval, posts the counter-entry once approved">
                    <Button size="small" icon={<UndoOutlined />} onClick={() => handleReverse(record)} />
                  </Tooltip>
                )}
                <Tooltip title={record.referenceId
                  ? 'Delete — sends a request for approval. Once approved it cascade-rolls-back the source record, fund balances and all related entries'
                  : 'Delete — sends a request for approval'}>
                  <Button
                    size="small"
                    danger
                    icon={<DeleteOutlined />}
                    onClick={() => handleDelete(record)}
                  />
                </Tooltip>
              </>
            )}
          </Space>
        );
      },
    },
  ];

  /* ── Delete / reverse request queue ── */
  const pendingActionCount = Object.keys(pendingActions).length;

  const actionStatusColors = {
    PENDING: 'gold', APPROVED: 'green', REJECTED: 'red', CANCELLED: 'default', FAILED: 'volcano',
  };

  const actionRequestColumns = [
    { title: 'Reference', dataIndex: 'reference', width: 120 },
    {
      title: 'Action', dataIndex: 'action', width: 100,
      render: (a) => <Tag color={a === 'DELETE' ? 'red' : 'orange'}>{a}</Tag>,
    },
    { title: 'Entry', dataIndex: 'entryIdSnapshot', width: 120 },
    { title: 'Narration', dataIndex: 'narrationSnapshot', ellipsis: true },
    {
      title: 'Amount', dataIndex: 'amountSnapshot', width: 130, align: 'right',
      render: (v) => `₹${fmt(v)}`,
    },
    {
      title: 'Moves Funds', dataIndex: 'hasFundSync', width: 110, align: 'center',
      render: (v) => v ? <Tag color="blue">Funds</Tag> : <Tag>GL only</Tag>,
    },
    { title: 'Reason', dataIndex: 'reason', ellipsis: true, render: (r) => r || <Text type="secondary">—</Text> },
    {
      title: 'Requested By', dataIndex: ['createdBy', 'name'], width: 150,
      render: (n) => n || <Text type="secondary">—</Text>,
    },
    {
      title: 'Requested On', dataIndex: 'createdAt', width: 140,
      render: (d) => d ? dayjs(d).format('DD/MM/YYYY HH:mm') : '—',
    },
    {
      title: 'Status', dataIndex: 'status', width: 120,
      render: (s, r) => (
        <Tooltip title={r.failureReason || (r.resultSummary || []).join(' · ') || ''}>
          <Tag color={actionStatusColors[s]}>{s}</Tag>
        </Tooltip>
      ),
    },
    {
      title: '', width: 60, render: (_, r) => r.status === 'PENDING' && (
        <Tooltip title="Withdraw this request">
          <Button size="small" icon={<StopOutlined />} onClick={() => handleWithdrawAction(r)} />
        </Tooltip>
      ),
    },
  ];

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Title level={5} style={{ margin: 0 }}>Journal Entries</Title>
            <Segmented
              value={view}
              onChange={setView}
              options={[
                { label: 'Posted', value: 'posted' },
                { label: 'Manual Entries', value: 'requests' },
                {
                  label: pendingActionCount
                    ? `Delete / Reverse (${pendingActionCount})`
                    : 'Delete / Reverse',
                  value: 'actions',
                },
              ]}
            />
          </Space>
        </Col>
        <Col>
          <Space wrap>
            {view === 'posted' ? (
              <>
                <Select
                  style={{ width: 180 }}
                  placeholder="Filter by source"
                  value={sourceFilter}
                  onChange={(v) => { setSourceFilter(v); setPage(1); }}
                  allowClear
                  options={JOURNAL_ENTRY_SOURCES.map((s) => ({ value: s, label: s }))}
                />
                <Select
                  style={{ width: 130 }}
                  placeholder="Status"
                  value={statusFilter}
                  onChange={(v) => { setStatusFilter(v); setPage(1); }}
                  allowClear
                  options={['POSTED', 'DRAFT', 'REVERSED'].map((s) => ({ value: s, label: s }))}
                />
                <Select
                  style={{ width: 150 }}
                  value={dateBasis}
                  onChange={(v) => { setDateBasis(v); setPage(1); }}
                  options={[
                    { value: 'TRANSACTION', label: 'By Txn Date' },
                    { value: 'ENTRY', label: 'By Entry Date' },
                  ]}
                />
                <RangePicker value={dateRange} onChange={(v) => { setDateRange(v); setPage(1); }} format="DD/MM/YYYY" allowClear />
                <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetch} /></Tooltip>
              </>
            ) : view === 'actions' ? (
              <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetchActionRequests} /></Tooltip>
            ) : (
              <Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={fetchRequests} /></Tooltip>
            )}
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setManualOpen(true)}>New Manual Entry</Button>
          </Space>
        </Col>
      </Row>

      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 16 }} />}

      {view === 'requests' && (
        <DataTable persistId="accounting.journal-entries.requests"
          dataSource={(requests.data || []).map((r, i) => ({ ...r, key: r._id || i }))}
          columns={requestColumns}
          loading={reqLoading}
          size="small"
          pagination={{ pageSize: 15, showTotal: (t) => `${t} requests`, showSizeChanger: false }}
          locale={{ emptyText: <Empty description="No manual entries yet" /> }}
        />
      )}

      {view === 'actions' && (
        <>
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 12 }}
            message="Deleting or reversing a posted entry needs approval"
            description="Both go through the same Approval Engine as a manual journal entry. Nothing changes in the ledger — or in any fund, cash drawer or treasury balance — until an approver signs the request off."
          />
          <DataTable persistId="accounting.journal-entries.action-requests"
            dataSource={(actionRequests.data || []).map((r, i) => ({ ...r, key: r._id || i }))}
            columns={actionRequestColumns}
            loading={actionLoading}
            size="small"
            pagination={{ pageSize: 15, showTotal: (t) => `${t} requests`, showSizeChanger: false }}
            locale={{ emptyText: <Empty description="No delete or reverse requests yet" /> }}
          />
        </>
      )}

      {view === 'posted' && (
      <DataTable persistId="accounting.journal-entries.main" serverPaginated
        dataSource={(data.entries || []).map((e, i) => ({ ...e, key: e._id || i }))}
        columns={columns}
        loading={loading}
        size="small"
        pagination={{
          current: data.meta?.page || page,
          total: data.meta?.total || 0,
          pageSize: data.meta?.limit || 30,
          onChange: (p) => setPage(p),
          showTotal: (total) => `${total} entries`,
          showSizeChanger: false,
        }}
        expandable={{
          expandedRowRender: (record) => (
            <DataTable persistId="accounting.journal-entries.lines" showToolbar={false}
              dataSource={(record.lines || []).map((l, i) => ({ ...l, key: i }))}
              columns={[
                { title: 'Account', key: 'account', render: (_, l) => l.account ? `${l.account.code} — ${l.account.name}` : '—' },
                { title: 'Nature', key: 'nature', render: (_, l) => natureTag(l.account), width: 190 },
                { title: 'Debit', dataIndex: 'debit', render: (v) => v > 0 ? `₹${fmt(v)}` : '', align: 'right', width: 130 },
                { title: 'Credit', dataIndex: 'credit', render: (v) => v > 0 ? `₹${fmt(v)}` : '', align: 'right', width: 130 },
                { title: 'Narration', dataIndex: 'narration', ellipsis: true },
              ]}
              pagination={false}
              size="small"
            />
          ),
        }}
      />
      )}

      {/* Detail Modal */}
      <Modal
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={null}
        title={`Journal Entry: ${detailModal?.entryId}`}
        width={700}
      >
        {detailModal && (
          <>
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Entry ID">{detailModal.entryId}</Descriptions.Item>
              <Descriptions.Item label="Transaction Date">
                {detailModal.date ? dayjs(detailModal.date).format('DD/MM/YYYY') : '—'}
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>(reporting date)</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Entry Date">
                {detailModal.entryDate ? dayjs(detailModal.entryDate).format('DD/MM/YYYY HH:mm') : '—'}
                <Text type="secondary" style={{ fontSize: 11, marginLeft: 6 }}>(booked)</Text>
              </Descriptions.Item>
              <Descriptions.Item label="Source"><Tag color="blue">{detailModal.source}</Tag></Descriptions.Item>
              <Descriptions.Item label="Status"><Tag color={statusColors[detailModal.status]}>{detailModal.status}</Tag></Descriptions.Item>
              <Descriptions.Item label="Total Debit">₹{fmt(detailModal.totalDebit)}</Descriptions.Item>
              <Descriptions.Item label="Total Credit">₹{fmt(detailModal.totalCredit)}</Descriptions.Item>
              <Descriptions.Item label="Narration" span={2}>{detailModal.narration}</Descriptions.Item>
              <Descriptions.Item label="Created By">{detailModal.createdBy?.name || '—'}</Descriptions.Item>
              <Descriptions.Item label="Posted At">{detailModal.postedAt ? dayjs(detailModal.postedAt).format('DD/MM/YYYY HH:mm') : '—'}</Descriptions.Item>
              <Descriptions.Item label="Raised From" span={2}>
                {detailModal.referenceModel
                  ? <Tag>{detailModal.referenceModel}</Tag>
                  : <Text type="secondary">Entered directly</Text>}
              </Descriptions.Item>
              {(reversalIndex.reversedBy(detailModal) || reversalIndex.cancels(detailModal)) && (
                <Descriptions.Item label="Reversal" span={2}>
                  {reversalIndex.reversedBy(detailModal)
                    ? <>Reversed by <Tag color="volcano">{reversalIndex.reversedBy(detailModal)}</Tag></>
                    : <>Counter-entry cancelling <Tag color="volcano">{reversalIndex.cancels(detailModal)}</Tag></>}
                </Descriptions.Item>
              )}
            </Descriptions>
            <Divider orientation="left" style={{ fontSize: 13 }}>Lines</Divider>
            <DataTable persistId="accounting.journal-entries.detail-lines" showToolbar={false}
              dataSource={(detailModal.lines || []).map((l, i) => ({ ...l, key: i }))}
              columns={[
                { title: 'Account', key: 'account', render: (_, l) => l.account ? `${l.account.code} — ${l.account.name}` : '—' },
                { title: 'Debit', dataIndex: 'debit', render: (v) => v > 0 ? `₹${fmt(v)}` : '', align: 'right' },
                { title: 'Credit', dataIndex: 'credit', render: (v) => v > 0 ? `₹${fmt(v)}` : '', align: 'right' },
              ]}
              pagination={false}
              size="small"
            />
          </>
        )}
      </Modal>

      <ManualJournalModal
        open={manualOpen}
        onClose={() => setManualOpen(false)}
        onSubmitted={() => { setView('requests'); fetchRequests(); }}
      />
    </div>
  );
}
